import { differenceBy, intersectionBy } from 'lodash';
import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { type PublicClip, ShortUrl } from '@air/api';
import { CustomFieldTypeName, type SetCustomFieldValuesBaseOptions } from '@air/api/types';
import {
  contentCollectionFormSelector,
  contentCollectionSessionIdSelector,
  updateContentCollectionClipsAction,
} from '@air/feature-content-collection/redux';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useShortIdContext } from '@air/provider-short-id';
import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { ContentCollectionBaseCustomFieldDateInput } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldDateInput';
import { ContentCollectionBaseCustomFieldPlainText } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldPlainText';
import { ContentCollectionCustomFieldsSelect } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldsSelect';
import { useContentCollectionCustomFields } from '~/components/ContentCollection/hooks/useContentCollectionCustomFields';
import { valueIfAllAreEqual } from '~/components/ContentCollection/utils';
import { ASSET_DETAILS_INPUT, BOARD_COLLECTION_ASSET_DETAILS_MODAL } from '~/constants/testIDs';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';
import { useAirStore } from '~/utils/ReduxUtils';

export type ContentCollectionGalleryAssetDetailsModalProps = {
  clips: PublicClip[];
};

export const ContentCollectionGalleryAssetDetailsModal = memo(
  ({ clips, onClose }: AirModalProps<ContentCollectionGalleryAssetDetailsModalProps>) => {
    const dispatch = useDispatch();
    const store = useAirStore();
    const { shortId } = useShortIdContext();
    const [customFields, setCustomFields] = useState<AssignedCustomFieldValue[]>([]);
    const { data: workspaceCustomFields } = useContentCollectionCustomFields();
    const form = useSelector(contentCollectionFormSelector);

    /** Set initial values when opening the modal after custom fields have been added */
    useEffect(() => {
      if (workspaceCustomFields?.data && form) {
        const formFields = workspaceCustomFields.data.filter((cf) =>
          form.fields.find((f) => f.customFieldId === cf.id),
        );

        setCustomFields(
          formFields.map((cf) => {
            switch (cf.type.title) {
              case CustomFieldTypeName.singleSelect: {
                // when editing a selection, if each clip has the same value for a single select, set that as the initial value
                const singleSelectValues = clips.map(
                  ({ customFields }) => customFields?.find(({ id }) => id === cf.id)?.value,
                );
                const singleSelectValueIfAllAreEqual = valueIfAllAreEqual(singleSelectValues);

                return {
                  ...cf,
                  value: singleSelectValueIfAllAreEqual || null,
                  values: null,
                };
              }
              case CustomFieldTypeName.multiSelect: {
                // find intersecting custom values on ALL of the clips and set them as the initial values
                const multiSelectValues = clips.map(
                  ({ customFields }) => customFields?.find(({ id }) => id === cf.id)?.values || [],
                );
                const filteredValues = multiSelectValues.filter((val) => Array.isArray(val));
                const intersectingValues = intersectionBy(...filteredValues, 'id');

                return {
                  ...cf,
                  values: intersectingValues.length ? intersectingValues : null,
                  value: null,
                };
              }
              case CustomFieldTypeName.plainText: {
                // if the plain text value is the same across all clips, set that as the initial value
                const plainTextValues = clips.map(
                  ({ customFields }) => customFields?.find(({ id }) => id === cf.id)?.plainTextValue,
                );
                const plainTextValueIfAllAreEqual = valueIfAllAreEqual(plainTextValues);

                return {
                  ...cf,
                  values: null,
                  value: null,
                  plainTextValue: plainTextValueIfAllAreEqual,
                };
              }
              case CustomFieldTypeName.date: {
                // if the date value is the same across all clips, set that as the initial value
                const dates = clips.map(({ customFields }) => customFields?.find(({ id }) => id === cf.id)?.dateValue);
                const dateIfAllDatesAreEqual = valueIfAllAreEqual(dates);

                return {
                  ...cf,
                  values: null,
                  value: null,
                  dateValue: dateIfAllDatesAreEqual,
                };
              }
              default: {
                return {
                  ...cf,
                  value: null,
                  values: null,
                };
              }
            }
          }),
        );
      }
    }, [clips, form, workspaceCustomFields]);

    const saveChanges = useCallback(async () => {
      const sessionId = contentCollectionSessionIdSelector(store.getState());

      if (!sessionId) {
        return;
      }

      /** Compile all the custom fields set for the asset, to pass to the API */
      const apiCustomFields: SetCustomFieldValuesBaseOptions[] = customFields.map((cf) => {
        switch (cf.type.title) {
          case CustomFieldTypeName.singleSelect: {
            return {
              customFieldId: cf.id,
              valueId: cf.value?.id,
            };
          }
          case CustomFieldTypeName.multiSelect: {
            const filteredValues = clips
              .map(({ customFields }) => customFields?.find(({ id }) => id === cf.id)?.values || [])
              .filter((val) => Array.isArray(val));

            const intersectingValues = intersectionBy(...filteredValues, 'id');
            const valuesToAdd = differenceBy(cf.values || [], intersectingValues, 'id');
            const valueToRemove = differenceBy(intersectingValues, cf.values || [], 'id');

            return {
              customFieldId: cf.id,
              valueIdsToAdd: valuesToAdd.map(({ id }) => id),
              valueIdsToRemove: valueToRemove.map(({ id }) => id),
            };
          }
          case CustomFieldTypeName.plainText: {
            return {
              customFieldId: cf.id,
              plainTextValue: cf.plainTextValue,
            };
          }
          case CustomFieldTypeName.date: {
            return {
              customFieldId: cf.id,
              dateValue: cf.dateValue,
            };
          }
        }
      });

      ShortUrl.batchUpdateFormAssets(shortId, form.id, {
        formSessionId: sessionId,
        update: {
          customFields: apiCustomFields,
        },
        assetIds: clips.map(({ assetId }) => assetId),
      });

      dispatch(
        updateContentCollectionClipsAction({
          clips: clips.map((clip) => ({
            id: clip.id,
            customFields: customFields.map((cf) => {
              if (cf.type.title === CustomFieldTypeName.singleSelect) {
                // when editing a selection, if each clip has the same value for a single select, set that as the initial value
                if (cf.value) {
                  // if they've added a new value, update it
                  return {
                    ...cf,
                    assetId: clip.assetId,
                  };
                } else {
                  const clipValues = clips.map(
                    ({ customFields }) => customFields?.find(({ id }) => id === cf.id)?.value,
                  );
                  const allClipValuesAreEqual = clipValues.every((val, i) =>
                    i === 0 ? true : val === clipValues[i - 1],
                  );
                  const currentClipCFValue = clip.customFields?.find(({ id }) => id === cf.id)?.value;

                  return {
                    ...cf,
                    assetId: clip.assetId,
                    value: allClipValuesAreEqual && clipValues[0] ? null : (currentClipCFValue ?? null),
                  };
                }
              } else {
                return {
                  ...cf,
                  assetId: clip.assetId,
                };
              }
            }),
          })),
        }),
      );

      dispatch(resetSelectedItemsAction());

      onClose();
    }, [clips, customFields, dispatch, form, onClose, shortId, store]);

    useHotkeys('enter', saveChanges, {}, [saveChanges]);

    return (
      <Modal isOpen onDismiss={onClose} data-testid={BOARD_COLLECTION_ASSET_DETAILS_MODAL}>
        <header className="mb-6 flex items-center justify-between gap-4">
          <ModalTitle>Edit {pluralize('items', clips.length, true)}</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </header>
        {customFields.map((customField, i) => {
          return (
            <div
              key={customField.id}
              data-testid={`${ASSET_DETAILS_INPUT}-${customField.type.title}-${customField.name}`}
              style={{
                marginTop: i > 0 ? 16 : 0,
              }}
            >
              <div className="flex w-full flex-col items-start justify-start">
                {customField.type.title === CustomFieldTypeName.singleSelect ||
                customField.type.title === CustomFieldTypeName.multiSelect ? (
                  <ContentCollectionCustomFieldsSelect
                    clips={clips}
                    field={customField}
                    onSelectionChange={({ values }) => {
                      setCustomFields(
                        customFields.map((cf) => {
                          if (cf.id === customField.id) {
                            if (cf.type.title === CustomFieldTypeName.singleSelect) {
                              return {
                                ...customField,
                                value: values ? values[0] : null,
                              };
                            } else {
                              return {
                                ...customField,
                                values,
                              };
                            }
                          } else {
                            return cf;
                          }
                        }),
                      );
                    }}
                  />
                ) : customField.type.title === CustomFieldTypeName.plainText ? (
                  <ContentCollectionBaseCustomFieldPlainText
                    clips={clips}
                    field={customField}
                    onTextChange={(value) => {
                      setCustomFields(
                        customFields.map((cf) => {
                          if (cf.id === customField.id) {
                            return {
                              ...customField,
                              plainTextValue: value,
                            };
                          } else {
                            return cf;
                          }
                        }),
                      );
                    }}
                  />
                ) : (
                  <ContentCollectionBaseCustomFieldDateInput
                    clips={clips}
                    field={customField}
                    onDateSelection={(date) => {
                      setCustomFields(
                        customFields.map((cf) => {
                          if (cf.id === customField.id) {
                            return {
                              ...customField,
                              dateValue: date,
                            };
                          } else {
                            return cf;
                          }
                        }),
                      );
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
        <div className="mt-4 flex justify-end gap-2">
          <Button appearance="ghost" color="grey" size="large" onClick={onClose}>
            Cancel
          </Button>
          <Button size="large" onClick={saveChanges}>
            Save
          </Button>
        </div>
      </Modal>
    );
  },
);

ContentCollectionGalleryAssetDetailsModal.displayName = 'ContentCollectionGalleryAssetDetailsModal';
